import './DetectedApplications.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DataSecurityApplicationStatus } from '@sonar-software/sonar-core';
import { DetectedApplicationCard } from './DetectedApplicationCard';
import empty from 'images/empty-detected-apps.svg';
import { SonarCard } from '@ui/general';
import { SonarText } from '@ui/text';

import { AppStatusSelect, AppStatusSelectValue } from '@security/AppStatusSelect';
import { Button, Col, Row, Space } from 'antd';
import { Else, If, Then } from 'react-if';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import React, { useMemo, useState } from 'react';
import { useBoolean, useDataSecurityApps } from '@hooks';

export const DetectedApplications: React.FC = () => {
	const applications = useDataSecurityApps(false, true, true);
	const [ showHidden, setShowHidden ] = useBoolean(false);
	const [ statusFilter, setStatusFilter ] = useState<AppStatusSelectValue>(AppStatusSelectValue.all);

	const appsToShow = useMemo(() => applications
		.filter((app) =>
			app.status === DataSecurityApplicationStatus.Detected ||
			(showHidden && app.status === DataSecurityApplicationStatus.Deleted))
		.filter((app) => {
			if (statusFilter === AppStatusSelectValue.connected) {
				return !app.installed;
			}
			if (statusFilter === AppStatusSelectValue.installed) {
				return app.installed;
			}

			return true;
		})
		.map((app) => app.applicationId) ?? [],
	[ applications, showHidden, statusFilter ]);

	return (
		<SonarCard
			className={ classNames('detected-applications', { empty: appsToShow.length === 0 }) }
			extra={
				<>
					<AppStatusSelect
						onChange={ setStatusFilter }
						value={ statusFilter } />
					<Button
						icon={ showHidden ? <EyeOutlined /> : <EyeInvisibleOutlined /> }
						id='show_hidden-button'
						onClick={ () => setShowHidden.toggle() }
						type={ showHidden ? 'primary' : 'default' }>
						{ showHidden ?
							COPY_SPECIFIC.FILTER_HIDDEN :
							COPY_SPECIFIC.SHOW_HIDDEN }
					</Button>
				</>
			}
			extraAsRow
			title={ COPY_SPECIFIC.DETECTED_APPLICATIONS }
			titleClassName='h2'>
			<If condition={ appsToShow.length }>
				<Then>
					<Row className='detected-applications-list'>
						{ appsToShow.map(
							(id) =>
								<Col key={ id }>
									<DetectedApplicationCard applicationId={ id } />
								</Col>
						) }
					</Row>
				</Then>
				<Else>
					<Space
						direction='vertical'
						size='large'>
						<SonarText
							size={ 16 }
							weight='semibold'>{ COPY_SPECIFIC.ALL_APPS_MONITORED }
						</SonarText>
						<img
							alt={ COPY_SPECIFIC.ALL_APPS_MONITORED }
							className='empty-apps'
							src={ empty } />
					</Space>
				</Else>
			</If>
		</SonarCard>
	);
};
